// FILE: src/pages/CRMCustomerList.jsx
import React, { useState } from 'react';
import Card from '../components/ui/Card';
import DataTable from '../components/ui/DataTable';
import AddCustomerModal from '../components/ui/AddCustomerModal';

const CRMCustomerList = () => {
  const [viewMode, setViewMode] = useState('table'); // 'table' or 'grid'
  const [showAddCustomerModal, setShowAddCustomerModal] = useState(false);
  const [customers, setCustomers] = useState([]);

  // Initialize customers with initial data
  React.useEffect(() => {
    if (customers.length === 0) {
      setCustomers(initialCustomerData);
    }
  }, []);

  // Initial customer data
  const initialCustomerData = [
    {
      id: 1,
      name: 'John Smith',
      email: 'john.smith@email.com',
      phone: '+1 (555) 123-4567',
      company: 'Tech Corp',
      status: 'Active',
      lastContact: '2024-01-15',
      totalValue: '$15,450',
      avatar: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      location: 'New York, USA',
      joinDate: '2023-03-15'
    },
    {
      id: 2,
      name: 'Sarah Johnson',
      email: 'sarah.j@example.com',
      phone: '+1 (555) 987-6543',
      company: 'Design Studio',
      status: 'Pending',
      lastContact: '2024-01-12',
      totalValue: '$8,920',
      avatar: 'https://images.unsplash.com/photo-1494790108755-2616b612b47c?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      location: 'Los Angeles, USA',
      joinDate: '2023-05-22'
    },
    {
      id: 3,
      name: 'Michael Chen',
      email: 'm.chen@company.org',
      phone: '+1 (555) 456-7890',
      company: 'Global Inc',
      status: 'Active',
      lastContact: '2024-01-10',
      totalValue: '$23,100',
      avatar: 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      location: 'Chicago, USA',
      joinDate: '2023-01-10'
    },
    {
      id: 4,
      name: 'Emily Davis',
      email: 'emily.davis@business.com',
      phone: '+1 (555) 234-5678',
      company: 'Marketing Pro',
      status: 'Inactive',
      lastContact: '2024-01-08',
      totalValue: '$5,670',
      avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      location: 'Miami, USA',
      joinDate: '2023-07-18'
    },
    {
      id: 5,
      name: 'David Wilson',
      email: 'david.w@startup.io',
      phone: '+1 (555) 345-6789',
      company: 'Innovation Labs',
      status: 'Active',
      lastContact: '2024-01-14',
      totalValue: '$12,340',
      avatar: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      location: 'Seattle, USA',
      joinDate: '2023-04-03'
    },
    {
      id: 6,
      name: 'Lisa Anderson',
      email: 'l.anderson@enterprise.net',
      phone: '+1 (555) 567-8901',
      company: 'Enterprise Solutions',
      status: 'Pending',
      lastContact: '2024-01-11',
      totalValue: '$18,750',
      avatar: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?ixlib=rb-1.2.1&ixid=eyJhcHBfaWQiOjEyMDd9&auto=format&fit=facearea&facepad=2&w=256&h=256&q=80',
      location: 'Boston, USA',
      joinDate: '2023-06-12'
    }
  ];

  // Customer statistics
  const customerStats = [
    {
      title: 'Total Customers',
      value: '2,431',
      change: '+12.5%',
      changeType: 'increase',
      icon: '👥'
    },
    {
      title: 'Active Customers',
      value: '1,897',
      change: '+8.3%',
      changeType: 'increase',
      icon: '✅'
    },
    {
      title: 'New This Month',
      value: '234',
      change: '+15.7%',
      changeType: 'increase',
      icon: '🆕'
    },
    {
      title: 'Customer LTV',
      value: '$4,250',
      change: '+5.2%',
      changeType: 'increase',
      icon: '💰'
    }
  ];

  const customerColumns = [
    {
      key: 'avatar',
      label: '',
      render: (value, row) => (
        <img 
          src={value} 
          alt={row.name} 
          className="w-10 h-10 rounded-full object-cover"
        />
      ),
      sortable: false
    },
    {
      key: 'name',
      label: 'Customer',
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900 dark:text-white">{value}</div>
          <div className="text-sm text-gray-500 dark:text-gray-400">{row.email}</div>
        </div>
      )
    },
    { key: 'company', label: 'Company' },
    { key: 'phone', label: 'Phone' },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
          value === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
          value === 'Pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
          'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
        }`}>
          {value}
        </span>
      )
    },
    { key: 'totalValue', label: 'Total Value' },
    { key: 'lastContact', label: 'Last Contact' },
    {
      key: 'actions',
      label: 'Actions',
      render: () => (
        <div className="flex space-x-2">
          <button className="text-blue-600 hover:text-blue-700 text-sm">Edit</button>
          <button className="text-green-600 hover:text-green-700 text-sm">Contact</button>
          <button className="text-red-600 hover:text-red-700 text-sm">Delete</button>
        </div>
      ),
      sortable: false
    }
  ];

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Customer Management</h1>
          <p className="text-gray-600 dark:text-gray-400 mt-2">Manage your customer relationships and data</p>
        </div>
        <div className="flex space-x-3">
          <button className="px-4 py-2 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            Export
          </button>
          <button 
            onClick={() => setShowAddCustomerModal(true)}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Add Customer
          </button>
        </div>
      </div>

      {/* Customer Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {customerStats.map((stat, index) => (
          <Card key={index} className="hover:shadow-md transition-shadow">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900 rounded-lg flex items-center justify-center">
                  <span className="text-2xl">{stat.icon}</span>
                </div>
              </div>
              <div className="ml-4 flex-1">
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">{stat.title}</p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white">{stat.value}</p>
                <div className="flex items-center mt-1">
                  <span className={`text-sm font-medium ${
                    stat.changeType === 'increase' ? 'text-green-600' : 'text-red-600'
                  }`}>
                    {stat.change}
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400 ml-1">from last month</span>
                </div>
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Filters and Search */}
      <Card>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-4 sm:space-y-0">
          <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4">
            <div className="relative">
              <select className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option>All Status</option>
                <option>Active</option>
                <option>Pending</option>
                <option>Inactive</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
            
            <div className="relative">
              <select className="appearance-none bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg px-4 py-2 pr-8 text-gray-700 dark:text-gray-300 focus:ring-2 focus:ring-blue-500 focus:border-transparent">
                <option>All Companies</option>
                <option>Tech Corp</option>
                <option>Design Studio</option>
                <option>Global Inc</option>
                <option>Marketing Pro</option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={() => setViewMode('table')}
              className={`p-2 rounded-lg ${
                viewMode === 'table' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
              </svg>
            </button>
            <button
              onClick={() => setViewMode('grid')}
              className={`p-2 rounded-lg ${
                viewMode === 'grid' ? 'bg-blue-100 text-blue-600 dark:bg-blue-900 dark:text-blue-300' : 'text-gray-400 hover:text-gray-600'
              }`}
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
              </svg>
            </button>
          </div>
        </div>
      </Card>

      {/* Customer List */}
      {viewMode === 'table' ? (
        <DataTable 
          columns={customerColumns}
          data={customers}
          searchable={true}
          pagination={true}
          itemsPerPage={10}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {customers.map((customer) => (
            <Card key={customer.id} className="hover:shadow-md transition-shadow">
              <div className="text-center">
                <img 
                  src={customer.avatar} 
                  alt={customer.name} 
                  className="w-16 h-16 rounded-full mx-auto mb-4 object-cover"
                />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{customer.name}</h3>
                <p className="text-sm text-gray-500 dark:text-gray-400">{customer.email}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">{customer.company}</p>
                
                <div className="flex justify-center mb-4">
                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                    customer.status === 'Active' ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300' :
                    customer.status === 'Pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300' :
                    'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300'
                  }`}>
                    {customer.status}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Total Value</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{customer.totalValue}</div>
                  </div>
                  <div>
                    <div className="text-gray-500 dark:text-gray-400">Last Contact</div>
                    <div className="font-semibold text-gray-900 dark:text-white">{customer.lastContact}</div>
                  </div>
                </div>

                <div className="flex space-x-2">
                  <button className="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                    Contact
                  </button>
                  <button className="flex-1 px-3 py-2 bg-gray-200 text-gray-700 text-sm rounded-lg hover:bg-gray-300 dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500">
                    Edit
                  </button>
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {/* Add Customer Modal */}
      {showAddCustomerModal && (
        <AddCustomerModal 
          onClose={() => setShowAddCustomerModal(false)}
          onAddCustomer={(newCustomer) => {
            const customerWithId = {
              ...newCustomer,
              id: customers.length + 1,
              joinDate: new Date().toISOString().split('T')[0],
              lastContact: new Date().toISOString().split('T')[0]
            };
            setCustomers([...customers, customerWithId]);
            setShowAddCustomerModal(false);
          }}
        />
      )}
    </div>
  );
};

export default CRMCustomerList;